// components/LegalRepresentatives/ListeRepresentative.js
import { useState } from 'react';
import ReactDOM from 'react-dom';
import RepresentativesTable from './RepresentativesTable';
import RepresentativesFilters from './RepresentativesFilters';
import RepresentativesPagination from './RepresentativesPagination';
import ManagedAccountsModal from './ManagedAccountsModal';
import './style/LegalForm.css';
import { useLegalRepresentatives } from '../service/Hooks/useLegalRepresentatives';
import useSelectedDomain from '../service/Hooks/useSelectedDomain';

/**
 * Composant principal pour afficher la liste des représentants légaux d'un domaine
 * @param {Object} props - Propriétés du composant
 * @param {Function} props.onClose - Fonction pour fermer le modal
 */
const ListeRepresentative = ({ onClose }) => {
  // Récupérer le domaine sélectionné
  const selectedDomain = useSelectedDomain();
  
  // Utiliser le hook personnalisé pour gérer les représentants
  const {
    representatives,
    pagination,
    loading,
    error,
    filters,
    setFilters
  } = useLegalRepresentatives(selectedDomain?.id);

  // État pour le modal des comptes gérés
  const [managedAccountsModal, setManagedAccountsModal] = useState({
    isOpen: false,
    representative: null
  });

  /**
   * Gérer le changement de page
   * @param {number} newPage - Nouveau numéro de page
   */
  const handlePageChange = (newPage) => {
    setFilters({ page: newPage });
  };

  /**
   * Ouvrir le modal des comptes gérés pour un représentant
   * @param {Object} representative - Représentant sélectionné
   */
  const handleViewManagedAccounts = (representative) => {
    setManagedAccountsModal({
      isOpen: true,
      representative
    });
  };

  /**
   * Fermer le modal des comptes gérés
   */
  const handleCloseManagedAccounts = () => {
    setManagedAccountsModal({
      isOpen: false,
      representative: null
    });
  };

  // Si aucun domaine n'est sélectionné, afficher un message d'erreur
  if (!selectedDomain?.id) {
    return ReactDOM.createPortal(
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center py-5">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3"></i>
              <p className="text-muted">Aucun domaine sélectionné</p>
              <button className="btn btn-secondary" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <>
      {/* Modal principal */}
      {ReactDOM.createPortal(
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-custom">
            <div className="modal-content">
              
              {/* En-tête du modal */}
              <div className="modal-header bg-white text-white">
                <div className="d-flex align-items-center">
                  <i className="fas fa-user-tie me-2 text-primary"></i>
                  <h5 className="modal-title mb-0 text-dark">
                    Liste des Représentants Légaux
                    {pagination && (
                      <span className="badge bg-light text-primary ms-2">
                        {pagination.totalCount}
                      </span>
                    )}
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-primary"
                  onClick={onClose}
                  disabled={loading}
                ></button>
              </div>

              {/* Corps du modal */}
              <div className="modal-body">
                
                {/* Barre de filtres */}
                <RepresentativesFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  loading={loading}
                />

                {/* État de chargement */}
                {loading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2 text-muted">Chargement des représentants légaux...</p>
                  </div>
                )}

                {/* Message d'erreur */}
                {error && !loading && (
                  <div className="alert alert-danger text-center">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Tableau des représentants */}
                {!loading && !error && (
                  <RepresentativesTable
                    representatives={representatives}
                    onViewManagedAccounts={handleViewManagedAccounts}
                  />
                )}
              </div>

              {/* Pied de page avec pagination */}
              <div className="modal-footer flex-column">
                {pagination && pagination.totalPages > 1 && (
                  <RepresentativesPagination
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    loading={loading}
                  />
                )}
                <div className="w-100 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <strong>{selectedDomain.name}</strong>
                  </small>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    <i className="fas fa-times me-1"></i>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal des comptes gérés */}
      <ManagedAccountsModal
        isOpen={managedAccountsModal.isOpen}
        onClose={handleCloseManagedAccounts}
        representative={managedAccountsModal.representative}
      />
    </>
  );
};

export default ListeRepresentative;